import React, { FC, useCallback, useMemo } from 'react';
import cls from 'classnames';
import { ArticleDetailUtil, EArticleClass, TArticleDetail } from 'shared/models/article';
import PlayFill from 'icon/play_fill.svg';
import Heart from 'icon/heart.svg';
import { useCTADirectly } from '~/utils/cta';
import { EClickPosition } from 'shared/constant';
import { useNavigate } from '@remix-run/react';
import { useSharingUrl } from '~/utils/build-sharing-url';
import { useTranslation } from 'react-i18next';
import { UserAuthIcon } from '~/components/user-auth-icon';
import { useNumFormatter } from '@lemon8/web-app-shared/hook';

import './style.scss';

interface IArticleCardProps {
  article: TArticleDetail;
}

export const ArticleCard: FC<IArticleCardProps> = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formatNum = useNumFormatter();
  const { article } = props;

  const { title, content } = useMemo(() => {
    const res: { title: string; content: string } = {
      title: article.title,
      content: article.shortContent.replace(/&(lt|gt|nbsp|amp|quot);/gi, ''),
    };

    if (!article.title.trim()) {
      res.title = res.content;
      res.content = '';
    }

    if (!res.title.trim()) {
      res.title = t('default_title');
    }

    return res;
  }, [article]);

  const cover = useMemo(() => ArticleDetailUtil.getCoverImage(article), [article]);

  const articleUrl = useSharingUrl(`/${article.author.linkName}/${article.groupId}`);
  const handleClickCard = useCTADirectly({
    clickPosition: EClickPosition.RelatedPost,
    clickId: article.groupId,
    onClick: useCallback(() => {
      navigate(articleUrl);
    }, [articleUrl]),
  });

  const authorUrl = useSharingUrl(`/${article.author.linkName}`);

  const handleClickAuthor = useCTADirectly({
    clickPosition: EClickPosition.RelatedPost,
    clickId: article.groupId,
    onClick: useCallback(() => {
      navigate(authorUrl);
    }, [authorUrl]),
  });

  return (
    <div className="article-card" onClick={handleClickCard}>
      <div
        className={cls('thumb-container', {
          'show-video-icon': article.articleClass === EArticleClass.VIDEO,
        })}
      >
        <img loading="lazy" src={cover.url} />
        {article.articleClass === EArticleClass.VIDEO && <PlayFill className="play-icon" />}
      </div>
      <div className="main-container">
        <div className="main">
          <p className="title">{title}</p>
          <p className="content">{content}</p>
        </div>
        <div className="others">
          <div className="left">
            <span className="author" onClick={handleClickAuthor}>
              {article.author.nickName}
            </span>
            <UserAuthIcon authInfo={article.author.authInfo} width={12} height={12} marginLeft={2} />
          </div>
          <span className="divider" />
          <div className="right">
            <Heart className="icon-like" />
            <span>{`${formatNum(article.diggCount || 0)}`}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
